"use client";

import { IMenuGroup, ISubgroupReference, MenuGroup } from "@order-up/models";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useSelectedLocation,
  useSelectedTenant,
} from "../contexts/SelectionContext";
import { selectParentMenuGroups } from "../services/menuFromDocs";
import { menuGroupService } from "../services/menuGroups";

/**
 * Defines the query keys used for menu group related data.
 */
export const menuGroupKeys = {
  all: ["menu_groups"] as const,
  lists: () => [...menuGroupKeys.all, "list"] as const,
  listByRestaurant: (tenantId: number, locationNumericId: number) =>
    [...menuGroupKeys.lists(), { tenantId, restaurantId: locationNumericId }] as const,
  parentGroups: (tenantId: number, locationNumericId: number) =>
    [
      ...menuGroupKeys.listByRestaurant(tenantId, locationNumericId),
      "parents",
    ] as const,
  childGroups: (tenantId: number, locationNumericId: number, parentId: number) =>
    [
      ...menuGroupKeys.listByRestaurant(tenantId, locationNumericId),
      "children",
      parentId,
    ] as const,
  details: () => [...menuGroupKeys.all, "detail"] as const,
  detailById: (tenantId: number, locationNumericId: number, menuGroupId: number) =>
    [
      ...menuGroupKeys.details(),
      { tenantId, restaurantId: locationNumericId, menuGroupId },
    ] as const,
};

/**
 * A server-rendered page's copy of a menu query's result (see
 * `services/menuFromDocs.ts`). With it the query starts in the success state —
 * the menu paints without a browser Firestore read — and `initialDataUpdatedAt`
 * (when the SERVER read it) keeps `staleTime` honest, so a stale copy still
 * revalidates in the background. A failed revalidation keeps the seeded data.
 * Omitted → unchanged client fetch.
 */
export interface MenuQuerySeed<T> {
  initialData?: T;
  initialDataUpdatedAt?: number;
}

/**
 * Hook to fetch all menu groups for a specific tenant and restaurant
 * @param tenantId The numeric ID of the tenant
 * @param locationNumericId The numeric ID of the restaurant
 */
export const useGetMenuGroupsByRestaurantId = (
  tenantId: number,
  locationNumericId: number,
  options?: MenuQuerySeed<MenuGroup[]>,
) => {
  return useQuery({
    queryKey: menuGroupKeys.listByRestaurant(tenantId, locationNumericId),
    queryFn: () =>
      menuGroupService.getMenuGroupsByRestaurantId(tenantId, locationNumericId),
    enabled: !!tenantId && !!locationNumericId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    initialData: options?.initialData,
    initialDataUpdatedAt: options?.initialDataUpdatedAt,
  });
};

/**
 * Hook to fetch all menu groups for the selected restaurant
 * @returns Query result with all menu groups
 */
export const useGetMenuGroupsForSelectedLocation = () => {
  const { tenantId } = useSelectedTenant();
  const { restaurantId: locationNumericId } = useSelectedLocation();

  return useQuery({
    queryKey: menuGroupKeys.listByRestaurant(tenantId || 0, locationNumericId || 0),
    queryFn: () => {
      if (!tenantId || !locationNumericId) {
        throw new Error("No tenant or restaurant selected");
      }
      return menuGroupService.getMenuGroupsByRestaurantId(
        tenantId,
        locationNumericId,
      );
    },
    enabled: !!tenantId && !!locationNumericId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to fetch all LIVE parent-level menu groups for the selected restaurant:
 * root groups (no parentIds) that are not archived and not explicitly
 * inactive. Archived/deactivated legacy menus are excluded so they cannot
 * poison single-menu auto-selection or appear in menu pickers
 * (`isActive !== false` keeps legacy docs without the field visible,
 * matching `isMenuGroupAvailableForOnlineOrdering`'s convention).
 *
 * Provider-optional: falls back to SelectionContext when ids aren't passed.
 * @returns Query result with live parent menu groups
 */
export const useParentMenuGroups = (
  tenantIdProp?: number,
  locationNumericIdProp?: number,
  options?: MenuQuerySeed<MenuGroup[]>,
) => {
  const { tenantId: contextTenantId } = useSelectedTenant();
  const { restaurantId: contextLocationNumericId } = useSelectedLocation();

  // Use props if provided, otherwise fall back to context
  const tenantId = tenantIdProp ?? contextTenantId ?? undefined;
  const locationNumericId = locationNumericIdProp ?? contextLocationNumericId ?? undefined;

  return useQuery({
    queryKey: menuGroupKeys.parentGroups(tenantId || 0, locationNumericId || 0),
    queryFn: async () => {
      if (!tenantId || !locationNumericId) {
        throw new Error("No tenant or restaurant selected");
      }
      const allMenuGroups = await menuGroupService.getMenuGroupsByRestaurantId(
        tenantId,
        locationNumericId,
      );
      return selectParentMenuGroups(allMenuGroups);
    },
    enabled: !!tenantId && !!locationNumericId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    initialData: options?.initialData,
    initialDataUpdatedAt: options?.initialDataUpdatedAt,
  });
};

/**
 * Hook to fetch child menu groups for a specific parent group
 * @param parentId - The ID of the parent menu group
 * @returns Query result with child menu groups
 */
export const useChildMenuGroups = (parentId: number) => {
  const { tenantId } = useSelectedTenant();
  const { restaurantId: locationNumericId } = useSelectedLocation();

  return useQuery({
    queryKey: menuGroupKeys.childGroups(
      tenantId || 0,
      locationNumericId || 0,
      parentId,
    ),
    queryFn: async () => {
      if (!tenantId || !locationNumericId) {
        throw new Error("No tenant or restaurant selected");
      }
      const allMenuGroups = await menuGroupService.getMenuGroupsByRestaurantId(
        tenantId,
        locationNumericId,
      );
      // Filter for child groups of the specified parent
      return allMenuGroups.filter(
        (group: MenuGroup) =>
          group.parentIds && group.parentIds.includes(parentId),
      );
    },
    enabled: !!tenantId && !!locationNumericId && !!parentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to fetch a single menu group by its ID
 * @param tenantId The numeric ID of the tenant
 * @param locationNumericId The numeric ID of the restaurant
 * @param menuGroupId The numeric ID of the menu group
 */
export const useGetMenuGroupById = (
  tenantId: number,
  locationNumericId: number,
  menuGroupId: number,
) => {
  return useQuery({
    queryKey: menuGroupKeys.detailById(tenantId, locationNumericId, menuGroupId),
    queryFn: () =>
      menuGroupService.getMenuGroupById(tenantId, locationNumericId, menuGroupId),
    enabled: !!tenantId && !!locationNumericId && !!menuGroupId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to create a new menu group
 * @returns A mutation object from TanStack Query
 */
export const useCreateMenuGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      restaurantId: locationNumericId,
      menuGroupData,
    }: {
      tenantId: number;
      restaurantId: number;
      menuGroupData: IMenuGroup;
    }) =>
      menuGroupService.createMenuGroup(tenantId, locationNumericId, menuGroupData),
    onSuccess: (_, variables) => {
      // Invalidate the menu groups list for that restaurant
      queryClient.invalidateQueries({
        queryKey: menuGroupKeys.listByRestaurant(
          variables.tenantId,
          variables.restaurantId,
        ),
      });
      // Also invalidate parent groups cache since it's derived from the full list
      queryClient.invalidateQueries({
        queryKey: menuGroupKeys.parentGroups(
          variables.tenantId,
          variables.restaurantId,
        ),
      });
    },
  });
};

/**
 * Hook to create multiple menu groups in a batch
 * @returns A mutation object from TanStack Query
 */
export const useCreateMenuGroupsBatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      restaurantId: locationNumericId,
      menuGroupsData,
    }: {
      tenantId: number;
      restaurantId: number;
      menuGroupsData: IMenuGroup[];
    }) =>
      menuGroupService.createMenuGroupsBatch(
        tenantId,
        locationNumericId,
        menuGroupsData,
      ),
    onSuccess: (_, variables) => {
      // Invalidate the menu groups list for that restaurant
      queryClient.invalidateQueries({
        queryKey: menuGroupKeys.listByRestaurant(
          variables.tenantId,
          variables.restaurantId,
        ),
      });
      // Also invalidate parent groups cache since it's derived from the full list
      queryClient.invalidateQueries({
        queryKey: menuGroupKeys.parentGroups(
          variables.tenantId,
          variables.restaurantId,
        ),
      });
    },
  });
};

/**
 * Hook to update an existing menu group
 * @returns A mutation object from TanStack Query
 */
export const useUpdateMenuGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      restaurantId: locationNumericId,
      menuGroupId,
      menuGroupData,
    }: {
      tenantId: number;
      restaurantId: number;
      menuGroupId: number;
      menuGroupData: Partial<IMenuGroup>;
    }) =>
      menuGroupService.updateMenuGroup(
        tenantId,
        locationNumericId,
        menuGroupId,
        menuGroupData,
      ),
    onSuccess: (_, { tenantId, restaurantId, menuGroupId }) => {
      // Invalidate specific menu group
      queryClient.invalidateQueries({
        queryKey: menuGroupKeys.detailById(tenantId, restaurantId, menuGroupId),
      });
      // Invalidate menu groups list for the restaurant
      queryClient.invalidateQueries({
        queryKey: menuGroupKeys.listByRestaurant(tenantId, restaurantId),
      });
      // Also invalidate parent groups cache
      queryClient.invalidateQueries({
        queryKey: menuGroupKeys.parentGroups(tenantId, restaurantId),
      });
    },
  });
};

/**
 * Hook to delete a menu group
 * @returns A mutation object from TanStack Query
 */
export const useDeleteMenuGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      restaurantId: locationNumericId,
      menuGroupId,
    }: {
      tenantId: number;
      restaurantId: number;
      menuGroupId: number;
    }) => menuGroupService.deleteMenuGroup(tenantId, locationNumericId, menuGroupId),
    onSuccess: (_, { tenantId, restaurantId, menuGroupId }) => {
      // Invalidate specific menu group
      queryClient.invalidateQueries({
        queryKey: menuGroupKeys.detailById(tenantId, restaurantId, menuGroupId),
      });
      // Invalidate menu groups list for the restaurant
      queryClient.invalidateQueries({
        queryKey: menuGroupKeys.listByRestaurant(tenantId, restaurantId),
      });
      // Also invalidate parent groups cache
      queryClient.invalidateQueries({
        queryKey: menuGroupKeys.parentGroups(tenantId, restaurantId),
      });
    },
  });
};

/**
 * Hook to archive a menu group (soft delete)
 * @returns A mutation object from TanStack Query
 */
export const useArchiveMenuGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      restaurantId: locationNumericId,
      menuGroupId,
    }: {
      tenantId: number;
      restaurantId: number;
      menuGroupId: number;
    }) =>
      menuGroupService.archiveMenuGroup(tenantId, locationNumericId, menuGroupId),
    onSuccess: (_, { tenantId, restaurantId, menuGroupId }) => {
      queryClient.invalidateQueries({ queryKey: menuGroupKeys.all });
      queryClient.invalidateQueries({
        queryKey: menuGroupKeys.detailById(tenantId, restaurantId, menuGroupId),
      });
      queryClient.invalidateQueries({
        queryKey: menuGroupKeys.listByRestaurant(tenantId, restaurantId),
      });
      queryClient.invalidateQueries({
        queryKey: menuGroupKeys.parentGroups(tenantId, restaurantId),
      });
    },
  });
};

/**
 * Hook to restore an archived menu group
 * @returns A mutation object from TanStack Query
 */
export const useRestoreMenuGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      restaurantId: locationNumericId,
      menuGroupId,
    }: {
      tenantId: number;
      restaurantId: number;
      menuGroupId: number;
    }) =>
      menuGroupService.restoreMenuGroup(tenantId, locationNumericId, menuGroupId),
    onSuccess: (_, { tenantId, restaurantId, menuGroupId }) => {
      queryClient.invalidateQueries({ queryKey: menuGroupKeys.all });
      queryClient.invalidateQueries({
        queryKey: menuGroupKeys.detailById(tenantId, restaurantId, menuGroupId),
      });
      queryClient.invalidateQueries({
        queryKey: menuGroupKeys.listByRestaurant(tenantId, restaurantId),
      });
      queryClient.invalidateQueries({
        queryKey: menuGroupKeys.parentGroups(tenantId, restaurantId),
      });
    },
  });
};

/**
 * Hook to update subgroup overrides on multiple parent groups atomically.
 *
 * Used when configuring a sub-group's per-parent pricing, visibility, and
 * scheduling overrides from the management app's MenuGroupModal.
 *
 * @returns A mutation object from TanStack Query
 */
export const useUpdateSubgroupOverrides = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      tenantId,
      restaurantId: locationNumericId,
      subgroupId,
      overridesByParent,
    }: {
      tenantId: number;
      restaurantId: number;
      subgroupId: number;
      overridesByParent: Record<number, Partial<ISubgroupReference>[]>;
    }) =>
      menuGroupService.updateSubgroupOverrides(
        tenantId,
        locationNumericId,
        subgroupId,
        overridesByParent,
      ),
    onSuccess: (_, { tenantId, restaurantId }) => {
      // Invalidate the full menu groups list (parent docs were modified)
      queryClient.invalidateQueries({
        queryKey: menuGroupKeys.listByRestaurant(tenantId, restaurantId),
      });
      // Invalidate parent groups cache
      queryClient.invalidateQueries({
        queryKey: menuGroupKeys.parentGroups(tenantId, restaurantId),
      });
    },
  });
};
